import { toast } from 'react-toastify';

import { Utils } from '@treezor/design-system';

import i18n from '@/i18n';

import { IDashboardConfig } from '@/store/models/dashboard-config.model';

import { initializeDashboardConfig, validateDashboardConfig } from '@/utils/config.utils';
import { fetchQuery } from '@/utils/queryClient';
import { fetchMethods } from '@/types/FetchService.types';
import { ToastMessage } from '@/ui-kit/toaster/ToastMessage';

interface IDeterminateConfig {
  url: string;
  isQualif?: boolean;
  isLocal?: boolean;
}
export class DashboardConfigService {
  static getDashboardConfig = async (): Promise<IDashboardConfig | undefined> => {
    const host = window.location.host;
    try {
      const determinateConfig = this.determineConfig(host);

      const configResponse = await fetchQuery(
        ['DashboardConfig', { method: fetchMethods.GET }],
        determinateConfig.url,
        { userRole: 'CONFIG' },
      );

      let config = determinateConfig.isLocal
        ? await configResponse.text()
        : ((await configResponse.json()) as IDashboardConfig);

      if (configResponse.ok) {
        if (Utils.isJson(config)) {
          config = JSON.parse(config) as IDashboardConfig;
          config.site.isLocal = determinateConfig.isLocal;
        }

        if (determinateConfig.isQualif) {
          config = {
            ...config,
            site: {
              ...config.site,
              isLocal: determinateConfig.isLocal,
            },
            api: {
              ...config.api,
              oauth: {
                ...config.api.oauth,
                redirectUri: config.api.oauth.redirectUri.replace('dashboard', 'dashboardqa'),
              },
              sso: {
                ...config.api.sso,
                redirectUri: config.api.sso.redirectUri.replace('dashboard', 'dashboardqa'),
              },
            },
          };
        }

        const missingConfigs = validateDashboardConfig(config);
        if (Array.isArray(missingConfigs) && missingConfigs.length > 0) {
          toast.error(
            <ToastMessage
              message="Dashboard Configuration failed validation, please contact an administrator"
              documentation="https://docs.treezor.com/guide/dashboard/configuration.html#general-configuration"
              payload={{ missingConfigs }}
            />,
            { autoClose: false, closeButton: false, closeOnClick: false, onClose: () => window.location.reload() },
          );
          return;
        } else {
          return initializeDashboardConfig(config);
        }
      } else {
        toast.error(
          <ToastMessage
            message="Configuration not found, please contact an administrator"
            documentation="https://docs.treezor.com/guide/dashboard/configuration.html#general-configuration"
          />,
          { autoClose: false, closeButton: false, closeOnClick: false, onClose: () => window.location.reload() },
        );
      }
      return;
    } catch (err) {
      toast.error(
        <ToastMessage
          message="Configuration not found, please contact an administrator"
          documentation="https://docs.treezor.com/guide/dashboard/configuration.html#general-configuration"
          payload={err}
        />,
        { autoClose: false, closeButton: false, closeOnClick: false, onClose: () => window.location.reload() },
      );
    }
  };

  static determineConfig = (host: string): IDeterminateConfig => {
    if (!host) throw new Error(i18n.t('translation:pages.config.error.notPredictable'));
    const hostParts = host.split('.');

    /* Local */
    if (hostParts.length === 1 && hostParts[0].includes('localhost')) {
      return {
        url: `///${host}/config.json`,
        isLocal: true,
      };
    }

    /* Dev or Sandbox */
    if (hostParts.length === 4) {
      const environment = hostParts[1];

      const configHost = hostParts[0].split('-');
      if (configHost.length < 2) throw new Error(i18n.t('translation:pages.config.error.notPredictable'));

      const [application, clientNamePart, ...suffixClient] = configHost;
      const clientName = suffixClient.length > 0 ? `${clientNamePart}-${suffixClient.join('-')}` : clientNamePart;

      return {
        url: `https://${clientName}.${environment}.treezor.co/core-connect/dashboard/config`,
        isQualif: application === 'dashboardqa',
      };
    }

    /* Prod */
    if (hostParts.length === 5) {
      const environment = hostParts[2];
      const clientName = hostParts[1];

      return {
        url: `https://${clientName}.${environment}.treezor.co/core-connect/dashboard/config`,
      };
    }

    throw new Error(i18n.t('translation:pages.config.error.notPredictable'));
  };
}
